import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Building2 } from 'lucide-react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

const FitnessBusinessInfo = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    // Set org_name to DBA if provided, otherwise legal name
    const businessName = formData.business_dba || formData.business_legal_name;
    updateFormData({ org_name: businessName });
    nextStep();
  };

  const currentYear = new Date().getFullYear();

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Business Information</CardTitle>
            <CardDescription className="text-white/60">
              Tell us about your fitness business
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="business_legal_name">Business Legal Name (LLC) *</Label>
            <Input
              id="business_legal_name"
              data-testid="fitness-legal-name-input"
              value={formData.business_legal_name}
              onChange={(e) => updateFormData({ business_legal_name: e.target.value })}
              required
              className="mt-1.5"
              placeholder="Your Fitness Studio LLC"
            />
          </div>

          <div>
            <Label htmlFor="business_dba">DBA / Trade Name (if different)</Label>
            <Input
              id="business_dba"
              data-testid="fitness-dba-input"
              value={formData.business_dba}
              onChange={(e) => updateFormData({ business_dba: e.target.value })}
              className="mt-1.5"
              placeholder="e.g., Zen Yoga Studio"
            />
          </div>

          <div>
            <Label htmlFor="street_address">Mailing Address *</Label>
            <Input
              id="street_address"
              data-testid="fitness-address-input"
              value={formData.street_address}
              onChange={(e) => updateFormData({ street_address: e.target.value })}
              required
              className="mt-1.5"
              placeholder="123 Main Street, Suite 100"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2">
              <Label htmlFor="org_city">City *</Label>
              <Input
                id="org_city"
                data-testid="fitness-city-input"
                value={formData.org_city}
                onChange={(e) => updateFormData({ org_city: e.target.value })}
                required
                className="mt-1.5"
                placeholder="City"
              />
            </div>
            <div>
              <Label htmlFor="org_state">State *</Label>
              <select
                id="org_state"
                data-testid="fitness-state-select"
                value={formData.org_state}
                onChange={(e) => updateFormData({ org_state: e.target.value })}
                required
                className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select</option>
                {US_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="postal_code">ZIP *</Label>
              <Input
                id="postal_code"
                data-testid="fitness-zip-input"
                value={formData.postal_code}
                onChange={(e) => updateFormData({ postal_code: e.target.value })}
                required
                className="mt-1.5"
                placeholder="12345"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="year_business_started">Year Business Started *</Label>
              <Input
                id="year_business_started"
                type="number"
                data-testid="fitness-year-started-input"
                value={formData.year_business_started}
                onChange={(e) => updateFormData({ year_business_started: parseInt(e.target.value) || '' })}
                required
                className="mt-1.5"
                placeholder={currentYear.toString()}
                min="1900"
                max={currentYear}
              />
            </div>
            <div>
              <Label htmlFor="website">Website / Social Media (Optional)</Label>
              <Input
                id="website"
                data-testid="fitness-website-input"
                value={formData.website}
                onChange={(e) => updateFormData({ website: e.target.value })}
                className="mt-1.5"
                placeholder="https://yourstudio.com"
              />
            </div>
          </div>

          <ThemedButton 
            type="submit"
            data-testid="fitness-business-next-btn"
            variant="wizard" size="full"
          >
            Next: Fitness Club Type & Services
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessBusinessInfo;
